import React, {
  useState,
  useRef,
  useEffect,
  useCallback,
  useMemo,
} from "react";
import { createPortal } from "react-dom";
import * as XLSX from "xlsx";
import ReactLoading from "react-loading";
import Img from "../components/Img";
import import_logo from "../assets/import.svg";
import download_logo from "../assets/download.svg";
import { Link } from "react-router-dom";
// Same annotation modal + auto-annotation helper that Kalimba uses.
// Adjust these two paths if the files live elsewhere in your project.
import { TextAnnotatorModal } from "../components/TextAnnotator";
import { buildAutoAnnotations } from "../components/autoAnnotate";
// Mechanism vocabulary (class → category → term), built from the taxonomy document.
import { MECHANISM_TAXONOMY } from "../components/mechanismTaxonomy";
const API_BASE = "/api";

/*
  ── PITT BRAND COLOR PALETTE ──
  Primary:   Royal Blue #003594, Gold #FFB81C
  Secondary: New Sky #DBEEFF, Gray #C8C9C7, White #FFFFFF,
             Medium Blue #00205B, Bronze #B87333, Black #000000
  Accent:    Golden Haze #FFE939, Light Blue #66B2E3, Light Green #00AD6E,
             Orange #F7911E, Infrared #FF5B45, White Peach #FFDECD, Merlot #770538
*/

const styles = `
@import url('https://fonts.googleapis.com/css2?family=Mystery+Quest&display=swap');

* { margin: 0; padding: 0; box-sizing: border-box; }

body {
  font-family: Arial, sans-serif;
  background: #f4f5f7;
  min-height: calc(100vh - 64px);
}

.page-wrapper {
  display: flex;
  flex-direction: column;
  min-height: calc(100vh - 64px);
}

.body-layout {
  flex: 1;
  display: flex;
  min-height: 0;
  overflow: hidden;
  overflow: clip;
}

/* ── Sidebar ── */
.sidebar {
  width: 250px;
  min-width: 250px;
  background-color: #00205B;
  color: #DBEEFF;
  display: flex;
  flex-direction: column;
  overflow-y: auto;
}

.sidebar-brand {
  padding: 40px 18px 26px;
  border-bottom: 1px solid rgba(255,255,255,0.1);
  display: flex;
  align-items: center;
  gap: 12px;
  background-color: #003594;
}

.sidebar-brand-logo {
  width: 10px;
  height: 10px;
  background-color: #FFB81C;
  border-radius: 50%;
  flex-shrink: 0;
}

.sidebar-brand-title {
  font-size: 30px;
  font-family: 'Mystery Quest', cursive;
  color: #FFFFFF;
  letter-spacing: 1px;
  line-height: 1;
}

.sidebar-section {
  padding: 14px 0;
  border-bottom: 1px solid rgba(255,255,255,0.08);
}

.sidebar-section-title {
  font-size: 12px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.1em;
  color: #FFFFFF;
  margin-bottom: 6px;
  padding: 30px 10px 10px;
  border-bottom: 1px solid #FFFFFF;
}

.sidebar-item {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 8px 10px 8px 18px;
  font-size: 13px;
  color: #DBEEFF;
  cursor: pointer;
  transition: background 0.15s, color 0.15s;
  text-decoration: none;
  border: none;
  background: none;
  width: 100%;
  text-align: left;
}

.sidebar-item:hover {
  background: rgba(255,184,28,0.1);
  color: #fff;
}

.sidebar-item.active {
  background: rgba(255,184,28,0.15);
  color: #FFB81C;
  font-weight: 600;
}

.sidebar-arrow {
  margin-left: auto;
  font-size: 9px;
  transition: transform 0.25s;
  color: #66B2E3;
}

.sidebar-arrow.open {
  transform: rotate(90deg);
  color: #FFB81C;
}

.sidebar-children {
  overflow: hidden;
  max-height: 0;
  transition: max-height 0.3s ease;
}

.sidebar-children.open { max-height: 800px; }

.sidebar-child {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 7px 10px 7px 48px;
  font-size: 12px;
  color: rgba(219,238,255,0.8);
  cursor: pointer;
  transition: background 0.12s, color 0.12s;
  text-decoration: none;
  border: none;
  background: none;
  width: 100%;
  text-align: left;
}

.sidebar-child:hover { background: rgba(255,184,28,0.08); color: #fff; }
.sidebar-child.active { color: #FFB81C; font-weight: 600; background: rgba(255,184,28,0.12); }

.sidebar-child-arrow {
  margin-left: auto;
  font-size: 8px;
  transition: transform 0.25s;
  color: #66B2E3;
}

.sidebar-child-arrow.open { transform: rotate(90deg); color: #FFB81C; }

.sidebar-grandchildren { overflow: hidden; max-height: 0; transition: max-height 0.3s ease; }
.sidebar-grandchildren.open { max-height: 500px; }

.sidebar-grandchild {
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 6px 10px 6px 70px;
  font-size: 11px;
  color: rgba(219,238,255,0.6);
  cursor: pointer;
  transition: background 0.1s, color 0.1s;
  text-decoration: none;
  border: none;
  background: none;
  width: 100%;
  text-align: left;
  font-family: inherit;
}

.sidebar-grandchild:hover { background: rgba(255,184,28,0.06); color: #fff; }
.sidebar-grandchild.active { color: #FFB81C; font-weight: 600; }

.sidebar-grandchild-dot {
  width: 5px; height: 5px; border-radius: 50%;
  background: #66B2E3; flex-shrink: 0;
}

.sidebar-grandchild:hover .sidebar-grandchild-dot,
.sidebar-grandchild.active .sidebar-grandchild-dot { background: #FFB81C; }

.sidebar-footer {
  margin-top: auto;
  padding: 16px 18px;
  border-top: 1px solid rgba(255,255,255,0.08);
}

.sidebar-footer-text { font-size: 11px; color: #FFFFFF; line-height: 1.5; }

/* ── Main ── */
.main-area {
  flex: 1;
  padding: 50px 24px 32px;
  display: flex;
  flex-direction: column;
  gap: 16px;
  overflow: auto;
  min-width: 0;
}

.page-heading { font-size: 20px; font-weight: 700; color: #00205B; flex-shrink: 0; }
.page-subheading { font-size: 13px; color: #555; margin-top: 2px; }

/* ── Plain Table ── */
.table-outer-wrapper {
  max-width: 100%;
  overflow: auto;
  border: 2px solid #C8C9C7;
  border-radius: 6px;
  background: #fff;
  max-height: clamp(250px, 60vh, 75vh);
  flex-shrink: 0;
  position: relative;
}

.bio-table {
  border-collapse: collapse;
  width: max-content;
  min-width: 100%;
  font-size: 12px;
}

/* Sticky header */
.bio-table thead th {
  position: sticky;
  top: 0;
  z-index: 2;
  background-color: #003594;
  color: #fff;
  padding: 0;
  font-size: 11px;
  font-weight: 600;
  letter-spacing: 0.05em;
  border-right: 1px solid rgba(255,255,255,0.15);
  white-space: nowrap;
  vertical-align: top;
}

.bio-table thead th.actions-th {
  background-color: #FFB81C;
  color: #00205B;
  font-size: 13px;
  text-align: center;
}

.bio-table thead th.comment-th {
  background-color: #00AD6E;
  color: #000;
}

.bio-table thead th.phrase-th {
  background-color: #EC4899;
  color: #000;
}

.th-inner {
  display: flex;
  flex-direction: column;
  gap: 4px;
  padding: 8px 12px;
}

.th-inner input {
  padding: 2px 5px;
  font-size: 10px;
  width: 100%;
  box-sizing: border-box;
  border-radius: 3px;
  border: 1px solid rgba(255,255,255,0.3);
  background: rgba(255,255,255,0.88);
  color: #000;
  outline: none;
}

/* Rows */
.bio-table tbody tr {
  border-bottom: 1px solid #e5e7eb;
  cursor: default;
}

.bio-table tbody tr:nth-child(even) td { background-color: #fafbfc; }
.bio-table tbody tr:hover td { background-color: #DBEEFF; }
.bio-table tbody tr.editing td { background-color: #DBEEFF; }

.bio-table tbody tr.editing {
  /* no position/z-index: the Mechanism dropdown is rendered in a portal */
}

.bio-table tbody td {
  padding: 6px 12px;
  color: #000;
  border-right: 1px solid #C8C9C7;
  line-height: 1.4;
  vertical-align: middle;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  max-width: 300px;
}

/* Frozen # and Actions columns (stay visible when scrolling right) */
.bio-table tbody td.sticky-col {
  position: sticky;
  z-index: 1;
  background-color: #fff;
}
.bio-table thead th.sticky-col { z-index: 4; }
.bio-table .sticky-edge {
  box-shadow: inset -1px 0 0 #C8C9C7, 3px 0 5px -2px rgba(0,0,0,0.25);
}

/* Click-to-edit cells */
.bio-table tbody td.editable-cell { cursor: text; }
.bio-table tbody td.editable-cell:hover {
  outline: 1px dashed #66B2E3;
  outline-offset: -2px;
  background-color: #fff;
}
.bio-table tbody td.cell-editing {
  outline: 2px solid #003594;
  outline-offset: -2px;
  background-color: #fff !important;
}

/* Wide / expandable columns */
.bio-table tbody td.statements-cell {
  cursor: text;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  max-width: 400px;
}

.bio-table tbody td.statements-cell.expanded {
  white-space: normal;
  word-break: break-word;
  vertical-align: top;
}

.bio-table tbody td.phrase-cell {
  max-width: 200px;
}

.bio-table tbody td.comment-cell.expanded {
  background-color: rgba(0,173,110,0.06);
}

.bio-table tbody td.phrase-cell.expanded {
  background-color: rgba(236,72,153,0.07);
}

.expand-hint {
  font-size: 10px;
  font-weight: 600;
  opacity: 0.7;
  margin-top: 3px;
}

.expand-hint.statements { color: #003594; }
.expand-hint.comment    { color: #00AD6E; }
.expand-hint.phrase     { color: #EC4899; }

/* Actions cell */
.bio-table tbody td.actions-td {
  text-align: center;
  white-space: nowrap;
  min-width: 240px;
  width: 240px;
}

/* ── Inputs ── */
.edit-input {
  width: 100%;
  padding: 3px 6px;
  font-size: 12px;
  border: 1px solid #003594;
  border-radius: 4px;
  outline: none;
  background: #fff;
}

.edit-input:focus {
  border-color: #003594;
  box-shadow: 0 0 0 2px rgba(0,53,148,0.15);
}

.edit-select {
  width: 100%;
  padding: 3px 6px;
  font-size: 12px;
  border: 1px solid #003594;
  border-radius: 4px;
  outline: none;
  background: #fff;
  color: #000;
  cursor: pointer;
}

.edit-select:focus {
  border-color: #003594;
  box-shadow: 0 0 0 2px rgba(0,53,148,0.15);
}

.edit-multiselect {
  position: relative;
  width: 100%;
}

.edit-multiselect-display {
  width: 100%;
  padding: 3px 6px;
  font-size: 12px;
  border: 1px solid #003594;
  border-radius: 4px;
  outline: none;
  background: #fff;
  color: #000;
  cursor: pointer;
  min-height: 24px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.edit-multiselect-display.placeholder {
  color: #888;
}

.edit-multiselect-panel {
  z-index: 9999;
  background: #fff;
  border: 1px solid #003594;
  border-radius: 4px;
  box-shadow: 0 4px 12px rgba(0,0,0,0.18);
  max-height: 220px;
  overflow-y: auto;
  padding: 4px 0;
}

.edit-multiselect-search-wrap {
  padding: 4px 8px 6px;
  position: sticky;
  top: 0;
  background: #fff;
  border-bottom: 1px solid #C8C9C7;
}

.edit-multiselect-search {
  width: 100%;
  padding: 4px 6px;
  font-size: 12px;
  border: 1px solid #66B2E3;
  border-radius: 4px;
  outline: none;
  box-sizing: border-box;
}

.edit-multiselect-search:focus {
  border-color: #003594;
  box-shadow: 0 0 0 2px rgba(0,53,148,0.15);
}

.edit-multiselect-empty {
  padding: 8px 10px;
  font-size: 12px;
  color: #999;
  font-style: italic;
}

.edit-multiselect-option {
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 4px 10px;
  font-size: 12px;
  cursor: pointer;
  white-space: nowrap;
}

.edit-multiselect-option:hover {
  background: #DBEEFF;
}

.edit-multiselect-option input {
  cursor: pointer;
}

.edit-multiselect-clear {
  border-top: 1px solid #C8C9C7;
  color: #B87333;
  font-style: italic;
}

/* ── Grouped Mechanism vocabulary ── */
.mech-group-title {
  display: flex;
  align-items: center;
  gap: 6px;
  width: 100%;
  padding: 6px 10px;
  border: none;
  border-top: 1px solid #E5E7EB;
  background: #F0F4FB;
  color: #00205B;
  font-size: 12px;
  font-weight: 700;
  cursor: pointer;
  text-align: left;
}
.mech-group-title:hover { background: #DBEEFF; }
.mech-chevron { font-size: 8px; color: #003594; width: 10px; }
.mech-count {
  font-size: 10px;
  font-weight: 600;
  color: #fff;
  background: #66B2E3;
  border-radius: 10px;
  padding: 1px 7px;
}
.mech-section-title {
  padding: 5px 10px 2px 22px;
  font-size: 10px;
  font-weight: 700;
  letter-spacing: 0.06em;
  text-transform: uppercase;
  color: #8A8F98;
}
.edit-multiselect-panel .edit-multiselect-option { padding-left: 22px; }

.edit-textarea {
  width: 100%;
  resize: none;
  height: 72px;
  line-height: 1.5;
  font-size: 12px;
  border: 1px solid #003594;
  border-radius: 4px;
  outline: none;
  background: #fff;
  padding: 3px 6px;
}

.edit-textarea:focus {
  border-color: #003594;
  box-shadow: 0 0 0 2px rgba(0,53,148,0.15);
}

.edit-textarea.phrase-textarea {
  height: 48px;
}

/* ── Action Buttons ── */
.action-btn {
  padding: 4px 10px;
  border: 1px solid #00205B;
  border-radius: 4px;
  cursor: pointer;
  font-size: 11px;
  font-weight: 600;
  transition: all 0.2s;
  margin-right: 4px;
}

.edit-btn { background: #DBEEFF; color: #00205B; }
.edit-btn:hover { background: #66B2E3; color: #fff; }
.insert-btn { background: #FFB81C; color: #00205B; }
.insert-btn:hover { background: #F7911E; color: #fff; }
.dup-btn { background: #DBEEFF; color: #00205B; }
.dup-btn:hover { background: #66B2E3; color: #fff; }
.delete-btn { background: #FF5B45; color: #000; }
.delete-btn:hover { background: #770538; color: #fff; }
.save-btn { background: #00AD6E; color: #000; }
.save-btn:hover { background: #008a58; color: #fff; }
.cancel-btn { background: #C8C9C7; color: #000; }
.cancel-btn:hover { background: #999; color: #fff; }

.add-row-btn {
  padding: 10px 22px;
  border: none;
  border-radius: 6px;
  cursor: pointer;
  font-size: 14px;
  font-weight: bold;
  transition: all 0.25s;
  background: #003594;
  color: #fff;
  display: flex;
  align-items: center;
  gap: 6px;
}

.add-row-btn:hover { background: #00205B; box-shadow: 0 2px 8px rgba(0,53,148,0.3); }

.table-info { font-size: 12px; color: #555; padding: 4px 0; display: flex; justify-content: space-between; flex-shrink: 0; }

/* ── Footer ── */
.app-footer {
  background-color: #00205B;
  color: #DBEEFF;
  text-align: center;
  padding: 14px 24px;
  font-size: 13px;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 20px;
}

.app-footer a { color: #FFB81C; text-decoration: none; transition: color 0.2s; }
.app-footer a:hover { color: #FFE939; }
.footer-divider { color: #66B2E3; }

/* ── Empty state ── */
.empty-state { display: flex; flex-direction: column; align-items: center; justify-content: center; padding: 48px 0; color: #C8C9C7; font-size: 14px; gap: 12px; }
.empty-icon { font-size: 32px; opacity: 0.5; }

/* ── Import / Download ── */
.import-btn {
  padding: 10px 22px;
  border: none;
  border-radius: 6px;
  cursor: pointer;
  font-size: 14px;
  font-weight: bold;
  transition: all 0.25s;
  background: #F7911E;
  color: #00205B;
  display: flex;
  align-items: center;
  gap: 6px;
}

.import-btn:hover { background: #B87333; color: #fff; }

.download-btn {
  padding: 10px 22px;
  border: none;
  border-radius: 6px;
  cursor: pointer;
  font-size: 14px;
  font-weight: bold;
  transition: all 0.25s;
  background: #00AD6E;
  color: #00205B;
  display: flex;
  align-items: center;
  gap: 6px;
}

.download-btn:hover { background: #008a58; color: #fff; }
.download-btn:disabled { opacity: 0.4; cursor: not-allowed; }

.import-file-input { display: none; }

/* ── Convert Panel ── */
.convert-panel {
  background: #DBEEFF;
  border: 2px dashed #003594;
  border-radius: 8px;
  padding: 20px 24px;
  display: flex;
  flex-direction: column;
  gap: 14px;
  flex-shrink: 0;
}

.convert-panel-title { font-size: 14px; font-weight: 700; color: #00205B; display: flex; align-items: center; gap: 8px; }
.convert-panel-hint { font-size: 11px; color: #003594; margin-top: -4px; }

.convert-panel-body { display: flex; justify-content:space-between; align-items:center; gap: 12px; flex-wrap: wrap; }

.convert-file-name {
  font-size: 10px;
  color: #003594;
  font-weight: 600;
  padding: 4px 10px;
  border-radius: 4px;
  max-width: 260px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.convert-upload-btn {
  padding: 8px 18px;
  border: 1px solid #003594;
  border-radius: 6px;
  cursor: pointer;
  font-size: 13px;
  font-weight: 600;
  background: #fff;
  color: #003594;
  transition: all 0.2s;
  display: flex;
  align-items: center;
  gap: 6px;
  max-width: 120px;
}

.convert-upload-btn:hover { background: #DBEEFF; border-color: #00205B; }

.translate-btn {
  padding: 8px 24px;
  border: 1px solid #00205B;
  border-radius: 6px;
  cursor: pointer;
  font-size: 14px;
  font-weight: bold;
  transition: all 0.25s;
  background: #FFDECD;
  color: #00205B;
  display: flex;
  align-items: center;
  gap: 8px;
}

.translate-btn:hover { background: #F7911E; box-shadow: 0 2px 8px rgba(255,184,28,0.4); }
.translate-btn:disabled { opacity: 0.4; cursor: not-allowed; }

.convert-status { display: flex; align-items: center; gap: 8px; font-size: 13px; }
.convert-success { color: #00AD6E; font-weight: 600; }

/* ── Error Banner ── */
.error-banner {
  position: fixed;
  top: 20px;
  right: 20px;
  z-index: 10000;
  max-width: 480px;
  background-color: #FFDECD;
  border: 2px solid #FF5B45;
  border-radius: 8px;
  padding: 16px;
  box-shadow: 0 4px 16px rgba(0,0,0,0.2);
  animation: slideIn 0.3s ease-out;
}

@keyframes slideIn {
  from { transform: translateX(100%); opacity: 0; }
  to { transform: translateX(0); opacity: 1; }
}

.error-banner-content { display: flex; flex-direction: column; gap: 8px; }
.error-banner-header { display: flex; justify-content: space-between; align-items: center; font-weight: bold; color: #770538; font-size: 14px; }
.error-banner-message { color: #00205B; font-size: 13px; line-height: 1.5; }
.error-banner-close { cursor: pointer; font-size: 20px; color: #FF5B45; background: none; border: none; padding: 0; line-height: 1; }
.error-banner-close:hover { color: #770538; }
.error-columns { margin-top: 4px; padding: 8px 12px; background: #fff; border: 1px solid #FF5B45; border-radius: 4px; font-size: 12px; font-family: monospace; }
.error-columns-label { font-weight: 600; color: #770538; margin-bottom: 4px; font-family: Arial, sans-serif; }
.error-columns-list { color: #00205B; word-break: break-all; }

/* ── Warning Banner ── */
.warning-banner {
  position: fixed;
  top: 20px;
  right: 20px;
  z-index: 9999;
  max-width: 480px;
  background-color: #FFF8E1;
  border: 2px solid #FFB81C;
  border-radius: 8px;
  padding: 16px;
  box-shadow: 0 4px 16px rgba(0,0,0,0.15);
  animation: slideIn 0.3s ease-out;
}

.warning-banner-content { display: flex; flex-direction: column; gap: 8px; }
.warning-banner-header { display: flex; justify-content: space-between; align-items: center; font-weight: bold; color: #B87333; font-size: 14px; }
.warning-banner-message { color: #00205B; font-size: 13px; line-height: 1.5; }
.warning-banner-close { cursor: pointer; font-size: 20px; color: #B87333; background: none; border: none; padding: 0; line-height: 1; }
.warning-banner-close:hover { color: #F7911E; }
.warning-columns { margin-top: 4px; padding: 8px 12px; background: #fff; border: 1px solid #FFB81C; border-radius: 4px; font-size: 12px; font-family: monospace; }
.warning-columns-label { font-weight: 600; color: #B87333; margin-bottom: 4px; font-family: Arial, sans-serif; }
.warning-columns-list { color: #00205B; word-break: break-all; }

.button-row { display: flex; align-items: center; gap: 10px; flex-wrap: wrap; flex-shrink: 0; }

/* ── Welcome ── */
.welcome-container { flex: 1; display: flex; flex-direction: column; align-items: center; justify-content: center; gap: 16px; color: #C8C9C7; text-align: center; padding: 60px 20px; }
.welcome-icon { font-size: 56px; opacity: 0.4; }
.welcome-title { font-size: 22px; font-weight: 700; color: #003594; }
.welcome-subtitle { font-size: 14px; color: #00205B; max-width: 400px; line-height: 1.6; }

.info-btn-row { display: flex; align-items: center; gap: 8px; flex-shrink: 0; }

/* ── Table Loading Overlay ── */
.table-loading-overlay {
  position: absolute;
  top: 0; left: 0; right: 0; bottom: 0;
  background: rgba(255, 255, 255, 0.85);
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 12px;
  z-index: 10;
  border-radius: 6px;
}

.table-loading-text {
  font-size: 14px;
  font-weight: 600;
  color: #003594;
}

/* ── Toolbar above table ── */
.table-toolbar {
  padding: 6px 10px;
  display: flex;
  align-items: center;
  gap: 10px;
  border-bottom: 1px solid #C8C9C7;
  background: #fafbfc;
  position: sticky;
  top: 0;
  z-index: 3;
}

/* ── Unsaved Changes Modal ── */
.unsaved-modal-overlay {
  position: fixed;
  top: 0; left: 0; right: 0; bottom: 0;
  background: rgba(0,0,0,0.5);
  z-index: 20000;
  display: flex;
  align-items: center;
  justify-content: center;
  animation: fadeIn 0.2s ease-out;
}

@keyframes fadeIn {
  from { opacity: 0; }
  to { opacity: 1; }
}

.unsaved-modal-box {
  background: #fff;
  border-radius: 10px;
  padding: 32px 36px;
  max-width: 460px;
  width: 90%;
  box-shadow: 0 8px 32px rgba(0,0,0,0.25);
  border: 2px solid #FFB81C;
  animation: modalSlideUp 0.25s ease-out;
}

@keyframes modalSlideUp {
  from { transform: translateY(20px); opacity: 0; }
  to { transform: translateY(0); opacity: 1; }
}

.unsaved-modal-title {
  font-size: 18px;
  font-weight: 700;
  color: #00205B;
  margin-bottom: 8px;
  display: flex;
  align-items: center;
  gap: 8px;
}

.unsaved-modal-message {
  font-size: 14px;
  color: #333;
  line-height: 1.6;
  margin: 12px 0 20px;
}

.unsaved-modal-actions {
  display: flex;
  justify-content: flex-end;
  gap: 10px;
}

.unsaved-modal-stay-btn {
  padding: 8px 20px;
  border-radius: 6px;
  border: 1px solid #003594;
  background: #003594;
  color: #fff;
  font-weight: 600;
  font-size: 13px;
  cursor: pointer;
  transition: background 0.2s;
}

.unsaved-modal-stay-btn:hover { background: #00205B; }

.unsaved-modal-leave-btn {
  padding: 8px 20px;
  border-radius: 6px;
  border: 1px solid #FF5B45;
  background: #FF5B45;
  color: #fff;
  font-weight: 600;
  font-size: 13px;
  cursor: pointer;
  transition: background 0.2s;
}

.unsaved-modal-leave-btn:hover { background: #770538; }

/* ── Restore Banner ── */
.restore-banner {
  background: #FFF8E1;
  border: 2px solid #FFB81C;
  border-radius: 8px;
  padding: 12px 16px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  flex-shrink: 0;
  flex-wrap: wrap;
}

.restore-banner-text {
  font-size: 13px;
  color: #00205B;
  display: flex;
  align-items: center;
  gap: 8px;
}

.restore-banner-actions {
  display: flex;
  gap: 8px;
  align-items: center;
}

.restore-yes-btn {
  padding: 6px 16px;
  border: none;
  border-radius: 5px;
  background: #003594;
  color: #fff;
  font-weight: 600;
  font-size: 12px;
  cursor: pointer;
  transition: background 0.2s;
}
.restore-yes-btn:hover { background: #00205B; }

.restore-no-btn {
  padding: 6px 16px;
  border: 1px solid #B87333;
  border-radius: 5px;
  background: #fff;
  color: #B87333;
  font-weight: 600;
  font-size: 12px;
  cursor: pointer;
  transition: all 0.2s;
}
.restore-no-btn:hover { background: #FFDECD; }

.autosave-indicator {
  font-size: 11px;
  color: #00AD6E;
  display: flex;
  align-items: center;
  gap: 4px;
  flex-shrink: 0;
}
  .row-num-td {
  text-align: center;
  color: #555;
  font-weight: 600;
  width: 36px;
  min-width: 36px;
}
`;

// ─────────────────────────────────────────────
// COLUMN DEFINITIONS
// ─────────────────────────────────────────────

const EXECUTABLE_MODEL_COLUMN_NAMES = [
  "Element Name",
  "Element Type",
  "Element Subtype",
  "Element HGNC Symbol",
  "Element Database",
  "Element IDs",
  "Compartment",
  "Compartment ID",
  "Cell Line",
  "Cell Type",
  "Tissue Type",
  "Organism",
  "Positive Regulator List",
  "Positive Connection Type List",
  "Positive Mechanism List",
  "Positive Site List",
  "Negative Regulator List",
  "Negative Connection Type List",
  "Negative Mechanism List",
  "Negative Site List",
  "Score List",
  "Source List",
  "Statements List",
  "Paper IDs List",
  "Positive Regulation Rule",
  "Negative Regulation Rule",
  "Variable",
  "Value Type",
  "Levels",
  "Const OFF",
  "Const ON",
  "Increment",
  "Spontaneous",
  "Balancing",
  "Delay",
  "Update Group",
  "Update Rate",
  "Update Rank",
];

const INTERACTION_LIST_COLUMN_NAMES = [
  "Comment",
  "Phrase",
  "Statements",
  "Paper IDs",
  "Regulator Name",
  "Regulator Type",
  "Regulator Subtype",
  "Regulator HGNC Symbol",
  "Regulator Database",
  "Regulator ID",
  "Regulator Compartment",
  "Regulator Compartment ID",
  "Regulated Name",
  "Regulated Type",
  "Regulated Subtype",
  "Regulated HGNC Symbol",
  "Regulated Database",
  "Regulated ID",
  "Regulated Compartment",
  "Regulated Compartment ID",
  "Sign",
  "Connection Type",
  "Mechanism",
  "Site",
  "Cell Line",
  "Cell Type",
  "Tissue Type",
  "Organism",
  "Score",
  "Source",
];

const makeInitials = (cols) => {
  const o = {};
  cols.forEach((c) => {
    o[c] = "";
  });
  return o;
};
const EXECUTABLE_MODEL_INITIALS = makeInitials(EXECUTABLE_MODEL_COLUMN_NAMES);
const INTERACTION_LIST_INITIALS = makeInitials(INTERACTION_LIST_COLUMN_NAMES);
const makeRows = (init, n) => Array.from({ length: n }, () => ({ ...init }));

// ─────────────────────────────────────────────
// ANNOTATION LABELS (same as Kalimba)
// Each label is a table field; highlighting text in a statement and
// choosing a label writes that text into the matching field on save.
// ─────────────────────────────────────────────
const BIO_ANNOTATION_LABELS = [
  "Regulator Name",
  "Regulator Type",
  "Regulator Subtype",
  "Regulator Compartment",
  "Regulated Name",
  "Regulated Type",
  "Regulated Subtype",
  "Regulated Compartment",
  "Sign",
  "Connection Type",
  "Mechanism",
  "Site",
  "Cell Line",
  "Cell Type",
  "Tissue Type",
  "Organism",
];

// The annotation modal's label list for BioRECIPE: the 16 fields + "Phrase"
// (its text goes into the table's Phrase column). Kalimba has no Phrase column,
// so it keeps the plain 16-label list.
const BIORECIPE_LABELS = [...BIO_ANNOTATION_LABELS, "Phrase"];

const FIELD_TO_LABEL_MAP = {
  "Regulator Name": "Regulator Name",
  "Regulator Type": "Regulator Type",
  "Regulator Subtype": "Regulator Subtype",
  "Regulator Compartment": "Regulator Compartment",
  "Regulated Name": "Regulated Name",
  "Regulated Type": "Regulated Type",
  "Regulated Subtype": "Regulated Subtype",
  "Regulated Compartment": "Regulated Compartment",
  Sign: "Sign",
  "Connection Type": "Connection Type",
  Mechanism: "Mechanism",
  Site: "Site",
  "Cell Line": "Cell Line",
  "Cell Type": "Cell Type",
  "Tissue Type": "Tissue Type",
  Organism: "Organism",
};

// ─────────────────────────────────────────────
// ANNOTATABLE COLUMNS
// Only Statements are annotated. "Phrase" is one of the labels in the field
// list (only ONE phrase per statement); on save every highlighted label,
// including Phrase, is written into its table column.
// ─────────────────────────────────────────────
const ANNOTATABLE_COLUMNS = {
  Statements: {
    applyToFields: true,
    accent: "#003594",
    title: "Statement — highlight each field (and the Phrase)",
  },
};

// A row's annotation entry is { Statements: result, Phrase: result }.
// Older autosaves stored the Statements result directly; handle both.
const normalizeAnnEntry = (e) =>
  !e ? {} : Array.isArray(e.annotations) ? { Statements: e } : e;
const getFieldAnn = (map, idx, field) =>
  normalizeAnnEntry(map?.[idx])[field] || null;

// ─────────────────────────────────────────────
// DROPDOWN FIELD DEFINITIONS
// ─────────────────────────────────────────────
const MULTI_SELECT_FIELDS = new Set(["Mechanism"]);
// Multi-select fields that use a grouped, searchable vocabulary
const MULTI_SELECT_GROUPS = { Mechanism: MECHANISM_TAXONOMY };

const DROPDOWN_FIELDS = {
  Sign: ["", "positive", "negative", "unknown", "unaffected", "noDirection"],
  "Regulator Type": [
    "",
    "protein",
    "protein complex",
    "gene",
    "RNA",
    "chemical",
    "biological process",
    "protein family",
    "cell",
    "other"
  ],
  "Regulated Type": [
    "",
    "protein",
    "protein complex",
    "gene",
    "RNA",
    "chemical",
    "biological process",
    "protein family",
    "cell",
    "other"

  ],
  "Regulator Database": [
    "",
    "UniProt",
    "HGNC",
    "PubChem",
    "Ensembl",
    "GENCODE",
    "RefSeq",
    "Pfam",
    "InterPro",
    "GO",
    "MeSH",
  ],
  "Regulated Database": [
    "",
    "UniProt",
    "HGNC",
    "PubChem",
    "Ensembl",
    "GENCODE",
    "RefSeq",
    "Pfam",
    "InterPro",
    "GO",
    "MeSH",
  ],
  "Regulator Compartment": [
    "",
    "cytoplasm",
    "cytosol",
    "plasma membrane",
    "nucleus",
    "mitochondria",
    "endoplasmic reticulum",
    "extracellular",
  ],
  "Regulated Compartment": [
    "",
    "cytoplasm",
    "cytosol",
    "plasma membrane",
    "nucleus",
    "mitochondria",
    "endoplasmic reticulum",
    "extracellular",
  ],
  "Regulator Compartment ID": [
    "",
    "0005737",
    "0005829",
    "0005886",
    "0005634",
    "0005739",
    "0005783",
    "0005576",
  ],
  "Regulated Compartment ID": [
    "",
    "0005737",
    "0005829",
    "0005886",
    "0005634",
    "0005739",
    "0005783",
    "0005576",
  ],
  "Connection Type": ["", "direct", "indirect"],
  Source: ["", "expert", "literature", "database", "data"],
};

// Labels in the annotation window that offer a preset value list
const ANNOTATION_FIELD_OPTIONS = {
  ...Object.fromEntries(
    BIO_ANNOTATION_LABELS.filter((l) => DROPDOWN_FIELDS[l]).map((l) => [
      l,
      DROPDOWN_FIELDS[l].filter(Boolean),
    ]),
  ),
  Mechanism: [
    ...new Set(
      MECHANISM_TAXONOMY.flatMap((g) => g.sections.flatMap((s) => s.terms)),
    ),
  ],
};

// ─────────────────────────────────────────────
// AUTO-SAVE HOOK (localStorage, per-page)
// Saves the table rows and (for annotatable pages) the annotations.
// ─────────────────────────────────────────────

const AUTOSAVE_DEBOUNCE_MS = 1000;

const useAutoSave = (pageId, data, enabled, annotations) => {
  const timerRef = useRef(null);
  useEffect(() => {
    if (!enabled || !pageId) return;
    if (timerRef.current) clearTimeout(timerRef.current);
    timerRef.current = setTimeout(() => {
      if (!data || data.length === 0) return;
      const hasAnyData = data.some((row) =>
        Object.values(row).some((v) => v !== "" && v != null),
      );
      if (!hasAnyData) return;
      try {
        localStorage.setItem(
          `biorecipe_save_${pageId}`,
          JSON.stringify({
            data,
            annotations: annotations || {},
            savedAt: new Date().toISOString(),
          }),
        );
      } catch (e) {
        console.warn("Auto-save failed:", e);
      }
    }, AUTOSAVE_DEBOUNCE_MS);
    return () => clearTimeout(timerRef.current);
  }, [pageId, data, enabled, annotations]);
};

const loadSavedData = (pageId) => {
  if (!pageId) return null;
  try {
    const raw = localStorage.getItem(`biorecipe_save_${pageId}`);
    if (!raw) return null;
    return JSON.parse(raw);
  } catch {
    return null;
  }
};

const clearSavedData = (pageId) => {
  if (!pageId) return;
  localStorage.removeItem(`biorecipe_save_${pageId}`);
};

const formatSavedAt = (iso) => {
  if (!iso) return "";
  const d = new Date(iso);
  return d.toLocaleString(undefined, {
    month: "short",
    day: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
};

// ─────────────────────────────────────────────
// PAGE DEFINITIONS
// ─────────────────────────────────────────────

const PAGE_CONFIGS = {
  "executable-model-create": {
    title: "Create Executable Model",
    description: "Build a new executable BioRECIPE model.",
    columns: EXECUTABLE_MODEL_COLUMN_NAMES,
    dynamicColumnPrefixes: ["State List"],
    initialData: makeRows(EXECUTABLE_MODEL_INITIALS, 10),
    hasImportData: true,
  },
  "interaction-list-create": {
    title: "Create Interaction List",
    description:
      "Build a new interaction list in BioRECIPE format. Click any cell to edit it; click a statement's underlined text to annotate it.",
    columns: INTERACTION_LIST_COLUMN_NAMES,
    initialData: makeRows(INTERACTION_LIST_INITIALS, 10),
    hasImportData: true,
    annotatable: true,
  },
  "convert-exec-sbml": {
    title: "Convert Executable Model: SBML → BioRECIPE-EM",
    description:
      "Upload an SBML file and convert it to a BioRECIPE executable model.",
    columns: EXECUTABLE_MODEL_COLUMN_NAMES,
    dynamicColumnPrefixes: ["State List"],
    initialData: [],
    conversion: {
      apiEndpoint: `${API_BASE}/convert_sbml_to_model/`,
      acceptTypes: ".xml",
      fileLabel: "SBML file - Level 2, Version 4 (.xml)",
    },
    hasImportData: false,
  },
  "convert-exec-sbmlqual": {
    title: "Convert Executable Model: SBMLqual → BioRECIPE-EM",
    description:
      "Upload an SBMLqual file and translate it to a BioRECIPE executable model.",
    columns: EXECUTABLE_MODEL_COLUMN_NAMES,
    dynamicColumnPrefixes: ["State List"],
    initialData: [],
    conversion: {
      apiEndpoint: `${API_BASE}/convert_sbmlqual_to_model/`,
      acceptTypes: ".xml, .sbml",
      fileLabel: "SBMLqual file (.xml, .sbml)",
    },
    hasImportData: false,
  },
  "convert-exec-biopax": {
    title: "Convert Executable Model: BioPAX → BioRECIPE-EM",
    description:
      "Upload a BioPAX file and translate it to a BioRECIPE executable model.",
    columns: EXECUTABLE_MODEL_COLUMN_NAMES,
    dynamicColumnPrefixes: ["State List"],
    initialData: [],
    conversion: {
      apiEndpoint: `${API_BASE}/convert_biopax_to_model/`,
      acceptTypes: ".xml,.owl",
      fileLabel: "BioPAX file - Level 3 (.xml, .owl)",
    },
    hasImportData: false,
  },
  "convert-exec-il": {
    title: "Convert Executable Model: BioRECIPE-IL → BioRECIPE-EM",
    description:
      "Upload a BioRECIPE-IL file and translate it to a BioRECIPE executable model.",
    columns: EXECUTABLE_MODEL_COLUMN_NAMES,
    dynamicColumnPrefixes: ["State List"],
    initialData: [],
    conversion: {
      apiEndpoint: `${API_BASE}/convert_biorecipe_il_to_model/`,
      acceptTypes: ".xlsx",
      fileLabel: "BioRECIPE-IL file (.xlsx)",
    },
    hasImportData: false,
  },
  "convert-int-sif": {
    title: "Convert SIF → BioRECIPE-IL",
    description:
      "Upload a SIF file and translate it to a BioRECIPE interaction list.",
    columns: INTERACTION_LIST_COLUMN_NAMES,
    initialData: [],
    conversion: {
      apiEndpoint: `${API_BASE}/convert_sif_to_interactions/`,
      acceptTypes: ".sif",
      fileLabel: "SIF file (.sif)",
    },
    hasImportData: false,
  },
  "convert-int-reach": {
    title: "Convert REACH → BioRECIPE-IL",
    description:
      "Upload a REACH file and translate it to a BioRECIPE interaction list.",
    columns: INTERACTION_LIST_COLUMN_NAMES,
    initialData: [],
    conversion: {
      apiEndpoint: `${API_BASE}/convert_reach_to_interactions/`,
      acceptTypes: ".json",
      fileLabel: "REACH fries file (.json)",
    },
    hasImportData: false,
  },
  "convert-int-indra": {
    title: "Convert INDRA → BioRECIPE-IL",
    description:
      "Upload a INDRA file and translate it to a BioRECIPE interaction list.",
    columns: INTERACTION_LIST_COLUMN_NAMES,
    initialData: [],
    conversion: {
      apiEndpoint: `${API_BASE}/convert_indra_to_interactions/`,
      acceptTypes: ".xlsx",
      fileLabel: "INDRA file (.xlsx)",
    },
    hasImportData: false,
  },
  "convert-int-em": {
    title: "Convert BioRECIPE-EM → BioRECIPE-IL",
    description:
      "Upload a BioRECIPE-EM file and translate it to a BioRECIPE interaction list.",
    columns: INTERACTION_LIST_COLUMN_NAMES,
    initialData: [],
    conversion: {
      apiEndpoint: `${API_BASE}/convert_biorecipe_em_to_interactions/`,
      acceptTypes: ".xlsx",
      fileLabel: "BioRECIPE-EM file (.xlsx)",
    },
    hasImportData: false,
  },
};

// ─────────────────────────────────────────────
// SIDEBAR DATA
// ─────────────────────────────────────────────

const SIDEBAR_DATA = [
  {
    section: "Creation",
    items: [
      {
        label: "Create a BioRECIPE file",
        children: [
          { label: "Executable Model", pageId: "executable-model-create" },
          { label: "Interaction List", pageId: "interaction-list-create" },
        ],
      },
    ],
  },
  {
    section: "Conversion",
    items: [
      {
        label: "Convert to BioRECIPE",
        children: [
          {
            label: "Executable Model / KG",
            children: [
              { label: " SBML", pageId: "convert-exec-sbml" },
              { label: " SBMLqual", pageId: "convert-exec-sbmlqual" },
              { label: " BioPAX", pageId: "convert-exec-biopax" },
              { label: " BioRECIPE-IL", pageId: "convert-exec-il" },
            ],
          },
          {
            label: "Interaction List",
            children: [
              { label: " SIF", pageId: "convert-int-sif" },
              { label: " REACH", pageId: "convert-int-reach" },
              { label: " INDRA", pageId: "convert-int-indra" },
              { label: " BioRECIPE-EM", pageId: "convert-int-em" },
            ],
          },
        ],
      },
    ],
  },
];

// ─────────────────────────────────────────────
// SIDEBAR TREE
// ─────────────────────────────────────────────

const SidebarTree = ({ activePage, onPageSelect }) => {
  const [openItems, setOpenItems] = useState({});
  const [openChildren, setOpenChildren] = useState({});
  const toggle = (s, k) => s((p) => ({ ...p, [k]: !p[k] }));
  const leaf = (id) => {
    if (id) onPageSelect(id);
  };

  return (
    <>
      {SIDEBAR_DATA.map((sec) => (
        <div className="sidebar-section" key={sec.section}>
          <div className="sidebar-section-title">{sec.section}</div>
          {sec.items.map((item) => {
            const ik = `${sec.section}-${item.label}`,
              io = !!openItems[ik],
              hc = item.children?.length > 0,
              il = !hc && item.pageId;
            return (
              <div key={ik}>
                <button
                  className={`sidebar-item ${il && activePage === item.pageId ? "active" : ""}`}
                  onClick={() => {
                    if (il) leaf(item.pageId);
                    else if (hc) toggle(setOpenItems, ik);
                  }}
                >
                  <span>{item.label}</span>
                  {hc && (
                    <span className={`sidebar-arrow ${io ? "open" : ""}`}>
                      ▶
                    </span>
                  )}
                </button>
                {hc && (
                  <div className={`sidebar-children ${io ? "open" : ""}`}>
                    {item.children.map((ch) => {
                      const ck = `${ik}-${ch.label}`,
                        co = !!openChildren[ck],
                        hgc = ch.children?.length > 0,
                        cl = !hgc && ch.pageId;
                      return (
                        <div key={ck}>
                          <button
                            className={`sidebar-child ${cl && activePage === ch.pageId ? "active" : ""}`}
                            onClick={() => {
                              if (cl) leaf(ch.pageId);
                              else if (hgc) toggle(setOpenChildren, ck);
                            }}
                          >
                            <span>{ch.label}</span>
                            {hgc && (
                              <span
                                className={`sidebar-child-arrow ${co ? "open" : ""}`}
                              >
                                ▶
                              </span>
                            )}
                          </button>
                          {hgc && (
                            <div
                              className={`sidebar-grandchildren ${co ? "open" : ""}`}
                            >
                              {ch.children.map((gc) => {
                                const gk = `${ck}-${gc.label}`;
                                return (
                                  <button
                                    key={gk}
                                    type="button"
                                    className={`sidebar-grandchild ${activePage === gc.pageId ? "active" : ""}`}
                                    onClick={() => {
                                      if (gc.pageId) leaf(gc.pageId);
                                    }}
                                  >
                                    <span className="sidebar-grandchild-dot" />
                                    <span>{gc.label}</span>
                                  </button>
                                );
                              })}
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      ))}
    </>
  );
};

// ─────────────────────────────────────────────
// MULTI-SELECT DROPDOWN (checkbox list, comma-joined value)
// `defaultOpen` + `onClose` let the table open it as soon as the cell is
// clicked and know when the picker was dismissed.
// ─────────────────────────────────────────────

const PANEL_W = 360; // grouped picker is wider than its table cell
const PANEL_MAX_H = 380;

const MultiSelectDropdown = ({
  options = [],
  groups = null, // [{ group, definition, sections: [{ title, terms }] }]
  value,
  onChange,
  defaultOpen = false,
  onClose,
}) => {
  const [open, setOpen] = useState(defaultOpen);
  const [coords, setCoords] = useState(null);
  const [search, setSearch] = useState("");
  const [expanded, setExpanded] = useState(() => new Set()); // opened classes
  const triggerRef = useRef(null);
  const panelRef = useRef(null);
  const searchInputRef = useRef(null);
  const onCloseRef = useRef(onClose);
  onCloseRef.current = onClose;

  const closePanel = useCallback(() => {
    setOpen(false);
    onCloseRef.current?.();
  }, []);

  const updateCoords = useCallback(() => {
    if (!triggerRef.current) return;
    const rect = triggerRef.current.getBoundingClientRect();
    const below = window.innerHeight - rect.bottom - 12;
    const above = rect.top - 12;
    const flip = below < 220 && above > below; // open upwards near the bottom
    const width = groups ? Math.max(rect.width, PANEL_W) : rect.width;
    setCoords({
      top: rect.bottom + 2,
      bottom: window.innerHeight - rect.top + 2,
      left: Math.max(8, Math.min(rect.left, window.innerWidth - width - 8)),
      width,
      flip,
      maxH: Math.max(160, Math.min(PANEL_MAX_H, flip ? above : below)),
    });
  }, [groups]);

  useEffect(() => {
    if (!open) return;
    updateCoords();
    setSearch("");
    const focusTimer = setTimeout(() => {
      searchInputRef.current?.focus();
    }, 0);
    const handleOutside = (e) => {
      if (
        triggerRef.current &&
        !triggerRef.current.contains(e.target) &&
        panelRef.current &&
        !panelRef.current.contains(e.target)
      ) {
        closePanel();
      }
    };
    // Scroll INSIDE the panel must not close/move it; any other scroll
    // (the table, the page) just re-positions it.
    const handleScroll = (e) => {
      if (panelRef.current && panelRef.current.contains(e.target)) return;
      updateCoords();
    };
    document.addEventListener("mousedown", handleOutside);
    document.addEventListener("scroll", handleScroll, true);
    window.addEventListener("resize", updateCoords);
    return () => {
      clearTimeout(focusTimer);
      document.removeEventListener("mousedown", handleOutside);
      document.removeEventListener("scroll", handleScroll, true);
      window.removeEventListener("resize", updateCoords);
    };
  }, [open, updateCoords, closePanel]);

  const selected = value
    ? value
        .split(",")
        .map((s) => s.trim())
        .filter(Boolean)
    : [];

  const toggleOption = (opt) => {
    let next;
    if (selected.includes(opt)) next = selected.filter((s) => s !== opt);
    else next = [...selected, opt];
    onChange(next.join(", "));
  };

  const q = search.trim().toLowerCase();

  // ── grouped vocabulary ─────────────────────────────────────────────────
  const allTerms = useMemo(
    () =>
      groups
        ? new Set(groups.flatMap((g) => g.sections.flatMap((s) => s.terms)))
        : new Set(options),
    [groups, options],
  );
  const customSelected = selected.filter((t) => !allTerms.has(t));
  const exactMatch =
    q &&
    ([...allTerms].some((t) => t.toLowerCase() === q) ||
      selected.some((t) => t.toLowerCase() === q));

  const visibleGroups = groups
    ? groups
        .map((g) => ({
          g,
          total: new Set(g.sections.flatMap((s) => s.terms)).size,
          picked: new Set(
            g.sections.flatMap((s) => s.terms).filter((t) => selected.includes(t)),
          ).size,
          sections: g.sections
            .map((s) => ({
              title: s.title,
              terms: q
                ? s.terms.filter((t) => t.toLowerCase().includes(q))
                : s.terms,
            }))
            .filter((s) => s.terms.length > 0),
        }))
        .filter((x) => x.sections.length > 0)
    : [];

  const toggleGroup = (name) =>
    setExpanded((prev) => {
      const next = new Set(prev);
      if (next.has(name)) next.delete(name);
      else next.add(name);
      return next;
    });

  const flatOptions = options
    .filter((o) => o !== "")
    .filter((o) => o.toLowerCase().includes(q));

  const checkRow = (key, term) => (
    <label key={key} className="edit-multiselect-option">
      <input
        type="checkbox"
        checked={selected.includes(term)}
        onChange={() => toggleOption(term)}
      />
      <span style={{ whiteSpace: "normal" }}>{term}</span>
    </label>
  );

  return (
    <div className="edit-multiselect">
      <div
        ref={triggerRef}
        className={`edit-multiselect-display${selected.length === 0 ? " placeholder" : ""}`}
        onClick={() => (open ? closePanel() : setOpen(true))}
        title={selected.join(", ")}
      >
        {selected.length > 0 ? selected.join(", ") : "— select —"}
      </div>
      {open &&
        coords &&
        createPortal(
          <div
            ref={panelRef}
            className="edit-multiselect-panel"
            style={{
              position: "fixed",
              ...(coords.flip
                ? { bottom: coords.bottom }
                : { top: coords.top }),
              left: coords.left,
              width: coords.width,
              maxHeight: coords.maxH,
              overscrollBehavior: "contain",
            }}
          >
            <div className="edit-multiselect-search-wrap">
              <input
                ref={searchInputRef}
                type="text"
                className="edit-multiselect-search"
                placeholder={
                  groups ? "Search all mechanisms..." : "Search..."
                }
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                onClick={(e) => e.stopPropagation()}
                onKeyDown={(e) => {
                  if (e.key === "Escape") closePanel();
                }}
              />
              {selected.length > 0 && (
                <div
                  style={{
                    marginTop: 4,
                    fontSize: 11,
                    color: "#003594",
                    fontWeight: 600,
                  }}
                >
                  {selected.length} selected
                </div>
              )}
            </div>

            {groups ? (
              <>
                {/* selected terms that are not in the vocabulary (imported / typed) */}
                {customSelected.length > 0 && (
                  <div>
                    <div className="mech-group-title" style={{ cursor: "default" }}>
                      Custom terms
                    </div>
                    {customSelected.map((t) => checkRow(`custom-${t}`, t))}
                  </div>
                )}

                {visibleGroups.map(({ g, total, picked, sections }) => {
                  const isOpen = q ? true : expanded.has(g.group);
                  return (
                    <div key={g.group}>
                      <button
                        type="button"
                        className="mech-group-title"
                        onClick={() => !q && toggleGroup(g.group)}
                        title={g.definition}
                      >
                        <span className="mech-chevron">{isOpen ? "▼" : "▶"}</span>
                        <span style={{ flex: 1, textAlign: "left" }}>
                          {g.group}
                        </span>
                        <span className="mech-count">
                          {picked > 0 ? `${picked} / ${total}` : total}
                        </span>
                      </button>
                      {isOpen &&
                        sections.map((sec) => (
                          <div key={`${g.group}|${sec.title}`}>
                            {sec.title && <div className="mech-section-title">{sec.title}</div>}
                            {sec.terms.map((t) =>
                              checkRow(`${g.group}|${sec.title}|${t}`, t),
                            )}
                          </div>
                        ))}
                    </div>
                  );
                })}

                {q && visibleGroups.length === 0 && (
                  <div className="edit-multiselect-empty">No matches</div>
                )}

                {/* the taxonomy is extensible: allow a custom term */}
                {q && !exactMatch && (
                  <div
                    className="edit-multiselect-option"
                    style={{ color: "#003594", fontWeight: 600 }}
                    onClick={() => {
                      toggleOption(search.trim());
                      setSearch("");
                    }}
                  >
                    + Add “{search.trim()}” as a custom term
                  </div>
                )}
              </>
            ) : flatOptions.length === 0 ? (
              <div className="edit-multiselect-empty">No matches</div>
            ) : (
              flatOptions.map((opt) => checkRow(opt, opt))
            )}

            <div
              className="edit-multiselect-option edit-multiselect-clear"
              onClick={() => {
                onChange("");
                closePanel();
              }}
            >
              Clear selection
            </div>
          </div>,
          document.body,
        )}
    </div>
  );
};

// ─────────────────────────────────────────────
// ANNOTATABLE STATEMENT (same behavior as Kalimba's ExpandableCell)
// Click the statement text to open the annotation modal. The modal is
// rendered in a portal so table CSS (nowrap/ellipsis) doesn't leak into it,
// and its clicks are stopped so they don't toggle the cell's expand state.
// ─────────────────────────────────────────────

const AnnotatableText = ({
  value,
  row,
  savedData,
  onSaveResult,
  field = "Statements",
}) => {
  const [open, setOpen] = useState(false);
  const [hovered, setHovered] = useState(false);
  const cfg = ANNOTATABLE_COLUMNS[field] || ANNOTATABLE_COLUMNS.Statements;

  const text = String(value ?? "");
  const savedAnnotations = savedData?.annotations || [];
  const hasAnnotations = savedAnnotations.length > 0;

  // Stable reference while the modal is open (the modal resets its state
  // whenever this array's identity changes).
  const initialAnnotations = useMemo(() => {
    if (!open) return [];
    const saved = savedData?.annotations || [];
    if (saved.length > 0) return saved;
    // Pre-highlight from the row's fields only for statements; a phrase is
    // general text, so it starts empty.
    return field === "Statements"
      ? buildAutoAnnotations(
          text,
          row,
          BIO_ANNOTATION_LABELS,
          FIELD_TO_LABEL_MAP,
        )
      : [];
  }, [open, savedData, text, row, field]);

  return (
    <>
      <span
        onClick={(e) => {
          e.stopPropagation();
          setOpen(true);
        }}
        onMouseEnter={() => setHovered(true)}
        onMouseLeave={() => setHovered(false)}
        title="Click to annotate"
        style={{
          cursor: "pointer",
          textDecoration: hovered ? "underline solid" : "underline dotted",
          textUnderlineOffset: "3px",
          color: hovered ? "#FFB81C" : hasAnnotations ? "#F57C00" : cfg.accent,
          background: hovered ? "rgba(255,184,28,0.1)" : "transparent",
          borderRadius: 4,
          padding: hovered ? "1px 4px" : "1px 0",
          transition: "all 0.15s ease",
        }}
      >
        {hasAnnotations && (
          <span
            style={{
              display: "inline-flex",
              alignItems: "center",
              justifyContent: "center",
              width: 16,
              height: 16,
              borderRadius: "50%",
              background: hovered ? "#FFB81C" : "#FFB74D",
              color: "#fff",
              fontSize: 9,
              fontWeight: 700,
              marginRight: 4,
              verticalAlign: "middle",
            }}
          >
            {savedAnnotations.length}
          </span>
        )}
        {text}
      </span>

      {open &&
        createPortal(
          <div
            onClick={(e) => e.stopPropagation()}
            style={{ display: "contents" }}
          >
            <TextAnnotatorModal
              isOpen
              onClose={() => setOpen(false)}
              text={text}
              labels={BIORECIPE_LABELS}
              fieldOptions={ANNOTATION_FIELD_OPTIONS}
              initialAnnotations={initialAnnotations}
              initialGeneralNote={savedData?.generalNote || ""}
              initialEvaluation={savedData?.evaluation || null}
              rowData={row}
              applyToFields={cfg.applyToFields}
              title={cfg.title}
              onSave={(result) => {
                if (onSaveResult) onSaveResult(result);
              }}
            />
          </div>,
          document.body,
        )}
    </>
  );
};

// ─────────────────────────────────────────────
// PLAIN EDITABLE TABLE COMPONENT
// Click any cell to edit it in place:
//   Enter / click away → save     Esc → cancel
//   dropdowns save as soon as a value is picked
// ─────────────────────────────────────────────

const WIDE_COLUMNS = new Set(["Statements", "Statements List", "Comment", "Phrase"]);

// Explicit column widths. The table uses table-layout: fixed with a <colgroup>,
// so the header and rows can never drift out of alignment.
const ROW_NUM_WIDTH = 56;
const ACTIONS_WIDTH = 240;
const getColWidth = (header) =>
  header === "Phrase"
    ? 180
    : WIDE_COLUMNS.has(header)
      ? 400
      : Math.max(150, header.length * 8 + 40);

const EditableTable = ({
  data,
  setData,
  columns,
  filterText,
  setFilterText,
  conversion,
  isLoading,
  annotatable = false,
  annotationsMap = {},
  setAnnotationsMap = () => {},
}) => {
  // Expanded cells, keyed "rowIndex:column" — each cell expands on its own.
  const [expandedRows, setExpandedRows] = useState(new Set());
  const [editingCell, setEditingCell] = useState(null); // { row, col }
  const [draft, setDraft] = useState("");
  const skipCommitRef = useRef(false);

  // Collapse expanded cells only when rows are added/removed (not on every keystroke)
  useEffect(() => {
    setExpandedRows(new Set());
    setEditingCell(null);
  }, [data.length]);

  const toggleExpand = useCallback((idx) => {
    setExpandedRows((prev) => {
      const next = new Set(prev);
      if (next.has(idx)) next.delete(idx);
      else next.add(idx);
      return next;
    });
  }, []);

  // Filtered rows: array of [originalIndex, row]
  const filteredRows = useMemo(() => {
    return data
      .map((row, i) => [i, row])
      .filter(([, row]) =>
        Object.keys(filterText).every((key) => {
          const fv = (filterText[key] || "").trim().toLowerCase();
          if (!fv) return true;
          return String(row[key] || "")
            .toLowerCase()
            .includes(fv);
        }),
      );
  }, [data, filterText]);

  const hasFilter = Object.values(filterText).some((v) => v);

  // ── inline cell editing ──
  const startEdit = useCallback(
    (idx, col) => {
      skipCommitRef.current = false;
      setEditingCell({ row: idx, col });
      setDraft(String(data[idx]?.[col] ?? ""));
    },
    [data],
  );

  const applyCellValue = useCallback(
    (idx, col, value) => {
      const old = String(data[idx]?.[col] ?? "");
      if (old === value) return;
      setData((prev) =>
        prev.map((r, i) => (i === idx ? { ...r, [col]: value } : r)),
      );
      // Edited statement text no longer matches its annotation offsets
      if (annotatable && ANNOTATABLE_COLUMNS[col]) {
        setAnnotationsMap((prev) => {
          const entry = normalizeAnnEntry(prev[idx]);
          if (!entry[col]?.annotations?.length) return prev;
          return {
            ...prev,
            [idx]: { ...entry, [col]: { ...entry[col], annotations: [] } },
          };
        });
      }
    },
    [data, annotatable, setData, setAnnotationsMap],
  );

  const commitEdit = useCallback(() => {
    if (skipCommitRef.current || !editingCell) return;
    skipCommitRef.current = true;
    applyCellValue(editingCell.row, editingCell.col, draft);
    setEditingCell(null);
  }, [editingCell, draft, applyCellValue]);

  const cancelEdit = useCallback(() => {
    skipCommitRef.current = true;
    setEditingCell(null);
  }, []);

  const handleDelete = useCallback(
    (idx) => {
      if (window.confirm("Delete this row?")) {
        setEditingCell(null);
        setData((prev) => prev.filter((_, j) => j !== idx));
        // Keep annotations aligned with their rows
        setAnnotationsMap((prev) => {
          const updated = {};
          Object.entries(prev).forEach(([key, value]) => {
            const k = Number(key);
            if (k < idx) updated[k] = value;
            else if (k > idx) updated[k - 1] = value;
          });
          return updated;
        });
      }
    },
    [setData, setAnnotationsMap],
  );

  const handleInsertRow = useCallback(
    (idx) => {
      const newRow = {};
      columns.forEach((c) => {
        newRow[c] = "";
      });
      const insertAt = idx + 1;
      setEditingCell(null);
      setData((prev) => {
        const d = [...prev];
        d.splice(insertAt, 0, newRow);
        return d;
      });
      // Shift annotations of every row after the insertion point
      setAnnotationsMap((prev) => {
        const updated = {};
        Object.entries(prev).forEach(([key, value]) => {
          const k = Number(key);
          updated[k >= insertAt ? k + 1 : k] = value;
        });
        return updated;
      });
    },
    [columns, setData, setAnnotationsMap],
  );

  // Insert an exact copy of a row directly beneath it (annotations included)
  const handleDuplicateRow = useCallback(
    (idx) => {
      const insertAt = idx + 1;
      setEditingCell(null);
      setData((prev) => {
        const d = [...prev];
        d.splice(insertAt, 0, { ...prev[idx] });
        return d;
      });
      setAnnotationsMap((prev) => {
        const updated = {};
        Object.entries(prev).forEach(([key, value]) => {
          const k = Number(key);
          updated[k >= insertAt ? k + 1 : k] = value;
        });
        if (prev[idx]) updated[insertAt] = JSON.parse(JSON.stringify(prev[idx]));
        return updated;
      });
    },
    [setData, setAnnotationsMap],
  );

  // Save from the annotation modal. Annotations are stored per row AND per
  // column (Statements / Phrase). Only columns configured with applyToFields
  // (Statements) also write the highlighted text into the row's fields.
  const handleAnnotationSave = useCallback(
    (originalIndex, field, result) => {
      setAnnotationsMap((prev) => ({
        ...prev,
        [originalIndex]: {
          ...normalizeAnnEntry(prev[originalIndex]),
          [field]: result,
        },
      }));
      const updates = result.fieldUpdates || {};
      if (Object.keys(updates).length > 0) {
        // Functional update: always applies to the latest rows, never to a
        // stale copy captured when the modal was opened.
        setData((prev) =>
          prev.map((r, i) => (i === originalIndex ? { ...r, ...updates } : r)),
        );
      }
    },
    [setData, setAnnotationsMap],
  );

  const handleFilterChange = useCallback(
    (header, value) => {
      setFilterText((prev) => ({ ...prev, [header]: value }));
    },
    [setFilterText],
  );

  const clearFilters = useCallback(() => {
    setFilterText({});
  }, [setFilterText]);

  // Editor shown inside the cell that is being edited
  const renderEditor = (col, idx) => {
    if (MULTI_SELECT_FIELDS.has(col)) {
      return (
        <MultiSelectDropdown
          defaultOpen
          options={DROPDOWN_FIELDS[col] || []}
          groups={MULTI_SELECT_GROUPS[col] || null}
          value={data[idx]?.[col] ?? ""}
          onChange={(v) => applyCellValue(idx, col, v)}
          onClose={() => setEditingCell(null)}
        />
      );
    }
    if (DROPDOWN_FIELDS[col]) {
      return (
        <select
          autoFocus
          className="edit-select"
          value={draft}
          onChange={(e) => {
            applyCellValue(idx, col, e.target.value);
            setEditingCell(null);
          }}
          onBlur={() => setEditingCell(null)}
          onKeyDown={(e) => {
            if (e.key === "Escape") cancelEdit();
          }}
        >
          {DROPDOWN_FIELDS[col].map((opt) => (
            <option key={opt} value={opt}>
              {opt === "" ? "— select —" : opt}
            </option>
          ))}
        </select>
      );
    }
    if (WIDE_COLUMNS.has(col)) {
      return (
        <textarea
          autoFocus
          className={`edit-textarea${col === "Phrase" ? " phrase-textarea" : ""}`}
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          onFocus={(e) =>
            e.target.setSelectionRange(
              e.target.value.length,
              e.target.value.length,
            )
          }
          onBlur={commitEdit}
          onKeyDown={(e) => {
            if (e.key === "Escape") cancelEdit();
            else if (e.key === "Enter" && !e.shiftKey) {
              e.preventDefault();
              commitEdit();
            }
          }}
        />
      );
    }
    return (
      <input
        autoFocus
        className="edit-input"
        type="text"
        value={draft}
        onChange={(e) => setDraft(e.target.value)}
        onBlur={commitEdit}
        onKeyDown={(e) => {
          if (e.key === "Enter") commitEdit();
          if (e.key === "Escape") cancelEdit();
        }}
      />
    );
  };

  if (!data || data.length === 0) {
    return (
      <div className="table-outer-wrapper">
        <div className="empty-state">
          <div className="empty-icon">🗂️</div>
          <div>
            {conversion
              ? 'Upload a source file and click "Translate to BioRECIPE".'
              : 'Click "Add Row" or "Import Data" to start.'}
          </div>
        </div>
        {isLoading && (
          <div className="table-loading-overlay">
            <ReactLoading type="spin" color="#003594" height={40} width={40} />
            <span className="table-loading-text">Loading data...</span>
          </div>
        )}
      </div>
    );
  }

  const totalWidth =
    ROW_NUM_WIDTH +
    ACTIONS_WIDTH +
    columns.reduce((sum, c) => sum + getColWidth(c), 0);

  return (
    <div style={{ minWidth: 0, maxWidth: "100%" }}>
      {/* Toolbar lives OUTSIDE the table (a <div> inside <thead> is invalid
          HTML and creates a stray anonymous cell in the first column). */}
      <div
        className="table-toolbar"
        style={{
          position: "static",
          border: "2px solid #C8C9C7",
          borderBottom: "none",
          borderRadius: "6px 6px 0 0",
        }}
      >
        {hasFilter && (
          <button
            onClick={clearFilters}
            style={{
              padding: "3px 10px",
              fontSize: "11px",
              border: "1px solid #C8C9C7",
              borderRadius: "4px",
              background: "#fff",
              cursor: "pointer",
              color: "#00205B",
            }}
          >
            Clear Filters
          </button>
        )}
        <span style={{ fontSize: "12px", color: "#555" }}>
          Showing {filteredRows.length} of {data.length} rows
        </span>
      </div>

      <div
        className="table-outer-wrapper"
        style={{ borderTopLeftRadius: 0, borderTopRightRadius: 0 }}
      >
        <table
          className="bio-table"
          style={{ tableLayout: "fixed", width: totalWidth }}
        >
          <colgroup>
            <col style={{ width: ROW_NUM_WIDTH }} />
            <col style={{ width: ACTIONS_WIDTH }} />
            {columns.map((c) => (
              <col key={c} style={{ width: getColWidth(c) }} />
            ))}
          </colgroup>
          <thead>
            <tr>
              <th
                className="actions-th sticky-col"
                style={{ backgroundColor: "#ccc", left: 0 }}
              >
                <div className="th-inner" style={{ alignItems: "center" }}>
                  <span>#</span>
                </div>
              </th>
              <th
                className="actions-th sticky-col sticky-edge"
                style={{ left: ROW_NUM_WIDTH }}
              >
                <div className="th-inner" style={{ alignItems: "center" }}>
                  <span>Actions</span>
                </div>
              </th>
              {columns.map((header) => (
                <th
                  key={header}
                  className={
                    header === "Comment"
                      ? "comment-th"
                      : header === "Phrase"
                        ? "phrase-th"
                        : ""
                  }
                  style={{
                    minWidth:
                      header === "Phrase"
                        ? 180
                        : WIDE_COLUMNS.has(header)
                          ? 400
                          : Math.max(150, header.length * 8 + 40),
                  }}
                >
                  <div className="th-inner">
                    <span>{header}</span>
                    <input
                      type="text"
                      placeholder="Filter..."
                      value={filterText[header] || ""}
                      onChange={(e) =>
                        handleFilterChange(header, e.target.value)
                      }
                      onClick={(e) => e.stopPropagation()}
                    />
                  </div>
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {filteredRows.map(([originalIndex, row], i) => {
              const rowEditing = editingCell?.row === originalIndex;
              return (
                <tr key={originalIndex} className={rowEditing ? "editing" : ""}>
                  <td className="row-num-td sticky-col" style={{ left: 0 }}>
                    {i + 1}
                  </td>

                  <td
                    className="actions-td sticky-col sticky-edge"
                    style={{ left: ROW_NUM_WIDTH }}
                  >
                    <button
                      className="action-btn insert-btn"
                      onClick={() => handleInsertRow(originalIndex)}
                      title="Insert a new row below this one"
                    >
                      + Insert
                    </button>
                    <button
                      className="action-btn dup-btn"
                      onClick={() => handleDuplicateRow(originalIndex)}
                      title="Insert a copy of this row below it"
                    >
                      ⧉ Duplicate
                    </button>
                    <button
                      className="action-btn delete-btn"
                      onClick={() => handleDelete(originalIndex)}
                    >
                      Delete
                    </button>
                  </td>

                  {columns.map((col) => {
                    const isComment = col === "Comment";
                    const isPhrase = col === "Phrase";
                    const isSpecial = WIDE_COLUMNS.has(col);
                    const isEditingThis =
                      rowEditing && editingCell.col === col;

                    // ── editing this cell ──
                    if (isEditingThis) {
                      return (
                        <td
                          key={col}
                          className="cell-editing"
                          style={{
                            padding: "4px 8px",
                            overflow: "visible",
                            maxWidth: "none",
                            textOverflow: "clip",
                          }}
                          onClick={(e) => e.stopPropagation()}
                        >
                          {renderEditor(col, originalIndex)}
                        </td>
                      );
                    }

                    // ── wide / expandable cells ──
                    if (isSpecial) {
                      const cellKey = `${originalIndex}:${col}`;
                      const isExpanded = expandedRows.has(cellKey);
                      const canAnnotate =
                        annotatable && ANNOTATABLE_COLUMNS[col] && row[col];
                      return (
                        <td
                          key={col}
                          className={`statements-cell editable-cell${isExpanded ? " expanded" : ""}${isComment && isExpanded ? " comment-cell" : ""}${isPhrase ? " phrase-cell" : ""}`}
                          title={isExpanded ? undefined : String(row[col] ?? "")}
                          onClick={() => startEdit(originalIndex, col)}
                        >
                          <div
                            style={{
                              overflow: isExpanded ? "visible" : "hidden",
                              textOverflow: isExpanded ? "unset" : "ellipsis",
                              whiteSpace: isExpanded ? "normal" : "nowrap",
                              wordBreak: isExpanded ? "break-word" : "normal",
                              minHeight: 16,
                            }}
                          >
                            {canAnnotate ? (
                              <AnnotatableText
                                field={col}
                                value={row[col]}
                                row={row}
                                savedData={getFieldAnn(
                                  annotationsMap,
                                  originalIndex,
                                  col,
                                )}
                                onSaveResult={(result) =>
                                  handleAnnotationSave(
                                    originalIndex,
                                    col,
                                    result,
                                  )
                                }
                              />
                            ) : (
                              row[col]
                            )}
                          </div>
                          {row[col] && (
                            <div style={{ display: "flex", gap: 10 }}>
                              <div
                                className={`expand-hint ${isComment ? "comment" : isPhrase ? "phrase" : "statements"}`}
                                role="button"
                                tabIndex={0}
                                onClick={(e) => {
                                  e.stopPropagation();
                                  toggleExpand(cellKey);
                                }}
                                onKeyDown={(e) => {
                                  if (e.key === "Enter" || e.key === " ") {
                                    e.preventDefault();
                                    toggleExpand(cellKey);
                                  }
                                }}
                                style={{ cursor: "pointer", userSelect: "none" }}
                              >
                                {isExpanded ? "▲ collapse" : "▼ expand"}
                              </div>
                              {canAnnotate && (
                                <div
                                  className="expand-hint statements"
                                  role="button"
                                  style={{
                                    cursor: "pointer",
                                    userSelect: "none",
                                  }}
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    startEdit(originalIndex, col);
                                  }}
                                >
                                  ✎ edit
                                </div>
                              )}
                            </div>
                          )}
                        </td>
                      );
                    }

                    // ── normal cells ──
                    return (
                      <td
                        key={col}
                        className="editable-cell"
                        title={String(row[col] ?? "")}
                        onClick={() => startEdit(originalIndex, col)}
                      >
                        {row[col]}
                      </td>
                    );
                  })}
                </tr>
              );
            })}
          </tbody>
        </table>

        {isLoading && (
          <div className="table-loading-overlay">
            <ReactLoading type="spin" color="#003594" height={40} width={40} />
            <span className="table-loading-text">Loading data...</span>
          </div>
        )}
      </div>
    </div>
  );
};

// ─────────────────────────────────────────────
// TABLE PAGE
// ─────────────────────────────────────────────

const TablePage = ({ pageId, config, registerPageData }) => {
  const fixedColumns = config.columns,
    dynamicPrefixes = config.dynamicColumnPrefixes || [],
    conversion = config.conversion || null,
    importButton = config.hasImportData;
  const isCreatePage = !!config.hasImportData;
  const annotatable = !!config.annotatable;

  const [dynamicColumns, setDynamicColumns] = useState([]);
  const [data, setData] = useState(config.initialData);
  const [annotationsMap, setAnnotationsMap] = useState({});
  const [filterText, setFilterText] = useState({});
  const [importError, setImportError] = useState(null);
  const [importWarning, setImportWarning] = useState(null);
  const [importLoading, setImportLoading] = useState(false);
  const fileInputRef = useRef(null);
  const [convertFile, setConvertFile] = useState(null);
  const [converting, setConverting] = useState(false);
  const [convertSuccess, setConvertSuccess] = useState(false);
  const convertFileRef = useRef(null);

  const [restoreInfo, setRestoreInfo] = useState(null);
  const [showRestoreBanner, setShowRestoreBanner] = useState(false);
  const [lastSavedAt, setLastSavedAt] = useState(null);

  const COLUMNS = [...fixedColumns, ...dynamicColumns];

  const isDyn = (c) => dynamicPrefixes.some((p) => c.startsWith(p));
  const emptyRow = () => {
    const r = {};
    COLUMNS.forEach((c) => {
      r[c] = "";
    });
    return r;
  };

  useEffect(() => {
    setData(config.initialData);
    setAnnotationsMap({});
    setDynamicColumns([]);
    setFilterText({});
    setImportError(null);
    setImportWarning(null);
    setImportLoading(false);
    setConvertFile(null);
    setConverting(false);
    setConvertSuccess(false);
    setLastSavedAt(null);

    if (isCreatePage) {
      const saved = loadSavedData(pageId);
      if (saved && saved.data?.length > 0) {
        const hasAny = saved.data.some((row) =>
          Object.values(row).some((v) => v !== "" && v != null),
        );
        if (hasAny) {
          setRestoreInfo(saved);
          setShowRestoreBanner(true);
        }
      }
    }
  }, [pageId, config.initialData, isCreatePage]);

  useAutoSave(pageId, data, isCreatePage, annotatable ? annotationsMap : null);

  useEffect(() => {
    if (!isCreatePage) return;
    const hasAny = data.some((row) =>
      Object.values(row).some((v) => v !== "" && v != null),
    );
    if (!hasAny) return;
    const timer = setTimeout(
      () => setLastSavedAt(new Date().toISOString()),
      1200,
    );
    return () => clearTimeout(timer);
  }, [data, annotationsMap, isCreatePage]);

  useEffect(() => {
    if (registerPageData) registerPageData(data);
  }, [data, registerPageData]);

  useEffect(() => {
    if (importWarning) {
      const t = setTimeout(() => setImportWarning(null), 10000);
      return () => clearTimeout(t);
    }
  }, [importWarning]);

  const handleRestore = () => {
    if (!restoreInfo) return;
    const saved = restoreInfo.data;
    const fc = Object.keys(saved[0] || {});
    const dyn = fc
      .filter((c) => !fixedColumns.includes(c) && isDyn(c))
      .sort((a, b) =>
        a.localeCompare(b, undefined, { numeric: true, sensitivity: "base" }),
      );
    setDynamicColumns(dyn);
    setData(saved);
    setAnnotationsMap(annotatable ? restoreInfo.annotations || {} : {});
    setLastSavedAt(restoreInfo.savedAt);
    setShowRestoreBanner(false);
    setRestoreInfo(null);
  };

  const handleDiscardRestore = () => {
    clearSavedData(pageId);
    setShowRestoreBanner(false);
    setRestoreInfo(null);
  };

  const handleClearSave = () => {
    if (
      window.confirm(
        "Clear all saved data for this page? This cannot be undone.",
      )
    ) {
      clearSavedData(pageId);
      setLastSavedAt(null);
      setData(config.initialData);
      setAnnotationsMap({});
      setDynamicColumns([]);
      setFilterText({});
    }
  };

  const handleConvertFileChange = (e) => {
    const f = e.target.files[0];
    if (f) {
      setConvertFile(f);
      setConvertSuccess(false);
    }
  };

  const handleTranslate = async () => {
    if (!convertFile || !conversion) return;
    setConverting(true);
    setConvertSuccess(false);
    setImportError(null);
    const fd = new FormData();
    fd.append("file", convertFile);
    try {
      const res = await fetch(conversion.apiEndpoint, {
        method: "POST",
        body: fd,
      });
      if (!res.ok) {
        let msg = `Conversion failed (${res.status})`;
        try {
          const j = JSON.parse(await res.text());
          msg = j.detail || j.error || j.message || msg;
        } catch {}
        throw new Error(msg);
      }
      const result = await res.json();
      const rows = result.dataframe || result.data || result;
      if (!Array.isArray(rows) || rows.length === 0)
        throw new Error("No data returned from conversion.");
      const rc = Object.keys(rows[0]);
      const fd2 = rc
        .filter((c) => !fixedColumns.includes(c) && isDyn(c))
        .sort((a, b) =>
          a.localeCompare(b, undefined, { numeric: true, sensitivity: "base" }),
        );
      const all = [...fixedColumns, ...fd2];
      const clean = rows.map((r) => {
        const o = {};
        all.forEach((c) => {
          o[c] = r[c] != null ? String(r[c]) : "";
        });
        return o;
      });
      setDynamicColumns(fd2);
      setData(clean);
      setAnnotationsMap({});
      setFilterText({});
      setConvertSuccess(true);
    } catch (err) {
      setImportError({
        title: err.name === "TypeError" ? "Network error" : "Conversion failed",
        message: err.message || "Failed to convert.",
      });
    } finally {
      setConverting(false);
    }
  };

  const handleImportClick = () => {
    if (fileInputRef.current) {
      fileInputRef.current.value = "";
      fileInputRef.current.click();
    }
  };

  const handleImportFile = (event) => {
    const file = event.target.files[0];
    if (!file) return;
    const ext = file.name.toLowerCase().slice(file.name.lastIndexOf("."));
    if (![".xlsx", ".xls"].includes(ext)) {
      setImportError({
        title: "Invalid file type",
        message: `Please upload .xlsx or .xls. Got "${ext}".`,
      });
      return;
    }
    setImportLoading(true);
    setImportWarning(null);
    const reader = new FileReader();
    reader.onload = (e) => {
      try {
        const wb = XLSX.read(e.target.result, { type: "array" }),
          ws = wb.Sheets[wb.SheetNames[0]];
        const json = XLSX.utils.sheet_to_json(ws, { defval: "" });
        if (!json?.length) {
          setImportError({
            title: "Empty file",
            message: "No data rows found.",
          });
          setImportLoading(false);
          return;
        }
        const fc = Object.keys(json[0]);
        const unexpectedCols = fc.filter(
          (c) =>
            c.trim() !== "" &&
            !/^__EMPTY(_\d+)?$/.test(c) &&
            !fixedColumns.includes(c) &&
            !isDyn(c) &&
            c !== "Comment",
        );
        if (unexpectedCols.length > 0) {
          setImportError({
            title: "Unrecognized columns",
            message:
              "The imported file contains columns that are not part of the BioRECIPE format.",
            unexpected: unexpectedCols,
            expected: fixedColumns,
            found: fc,
          });
          setImportLoading(false);
          return;
        }
        const miss = fixedColumns.filter(
          (c) => !fc.includes(c) && c !== "Comment",
        );
        if (miss.length > 0) {
          setImportWarning({
            title: "Missing columns",
            message:
              "Some expected BioRECIPE columns were not found. They have been added with empty values.",
            missing: miss,
          });
        }
        const dyn = fc
          .filter((c) => !fixedColumns.includes(c) && isDyn(c))
          .sort((a, b) =>
            a.localeCompare(b, undefined, {
              numeric: true,
              sensitivity: "base",
            }),
          );
        const stateListCols = dyn.filter((c) => c.startsWith("State List"));
        const zeroIndexed = stateListCols.filter(
          (c) => c.replace(/^State List\s*/, "") === "0",
        );
        if (zeroIndexed.length > 0) {
          setImportError({
            title: "Invalid State List numbering",
            message: "State List columns should start from 1, not 0.",
            found: stateListCols,
          });
          setImportLoading(false);
          return;
        }
        const all = [...fixedColumns, ...dyn];
        const clean = json.map((r) => {
          const o = {};
          all.forEach((c) => {
            o[c] = r[c] !== undefined ? String(r[c]) : "";
          });
          return o;
        });
        setDynamicColumns(dyn);
        setData(clean);
        setAnnotationsMap({});
        setFilterText({});
        setImportError(null);
      } catch (err) {
        setImportError({ title: "Read error", message: err.message });
      } finally {
        setImportLoading(false);
      }
    };
    reader.onerror = () => {
      setImportError({ title: "Read error", message: "Failed to read file." });
      setImportLoading(false);
    };
    reader.readAsArrayBuffer(file);
  };

  const handleDownload = () => {
    if (!data?.length) return;
    const exp = data.map((r) => {
      const o = {};
      COLUMNS.forEach((c) => {
        o[c] = r[c] ?? "";
      });
      return o;
    });
    const wb = XLSX.utils.book_new(),
      ws = XLSX.utils.json_to_sheet(exp, { header: COLUMNS });
    ws["!cols"] = COLUMNS.map((c) => ({ wch: Math.max(c.length + 2, 12) }));
    XLSX.utils.book_append_sheet(wb, ws, "BioRECIPE");

    // Annotations sheet (Kalimba layout + which column was annotated)
    if (annotatable && Object.keys(annotationsMap).length > 0) {
      const annotationRows = [];
      Object.entries(annotationsMap).forEach(([rowIdx, entry]) => {
        const idx = Number(rowIdx);
        const row = data[idx];
        if (!row) return;
        Object.entries(normalizeAnnEntry(entry)).forEach(([field, ann]) => {
          if (!ann) return;
          const base = {
            "Row #": idx + 1,
            "Annotated Column": field,
            Regulator: row["Regulator Name"] || "",
            Regulated: row["Regulated Name"] || "",
            "Source Text": row[field] || "",
            "Paper IDs": row["Paper IDs"] || "",
          };
          const tail = {
            "General Note": ann.generalNote || "",
            "Overall Rating": ann.evaluation?.overallRating || "",
            "Overall Comment": ann.evaluation?.evaluationNote || "",
          };
          const anns = ann.annotations || [];
          if (anns.length > 0) {
            const phraseAnn = anns.find(
              (x) => x.label === "Phrase" || x.kind === "phrase",
            );
            anns.forEach((a) => {
              const isPh = a === phraseAnn;
              const inside =
                phraseAnn &&
                !isPh &&
                a.start >= phraseAnn.start &&
                a.end <= phraseAnn.end;
              annotationRows.push({
                ...base,
                Phrase: phraseAnn?.text || "",
                "In Phrase": isPh ? "" : inside ? "Yes" : phraseAnn ? "No" : "",
                "Highlighted Text": a.text || "",
                Label: a.label || "",
                "Field Value": row[a.label] || "",
                "Annotation Note": a.note || "",
                ...tail,
              });
            });
          } else if (ann.generalNote || ann.evaluation?.overallRating) {
            annotationRows.push({
              ...base,
              Phrase: "",
              "In Phrase": "",
              "Highlighted Text": "",
              Label: "",
              "Field Value": "",
              "Annotation Note": "",
              ...tail,
            });
          }
        });
      });
      if (annotationRows.length > 0) {
        const wsAnn = XLSX.utils.json_to_sheet(annotationRows);
        wsAnn["!cols"] = Object.keys(annotationRows[0]).map((k) => ({
          wch: Math.max(k.length + 2, k === "Source Text" ? 50 : 18),
        }));
        XLSX.utils.book_append_sheet(wb, wsAnn, "Annotations");
      }
    }

    const today = new Date().toISOString().slice(0, 10);
    XLSX.writeFile(
      wb,
      `${config.title.replace(/[^a-zA-Z0-9]/g, "_")}_${today}.xlsx`,
    );
  };

  const haf = Object.values(filterText).some((v) => v);

  const handleAddRow = () => {
    setData((prev) => [...prev, emptyRow()]);
  };

  return (
    <>
      {showRestoreBanner && restoreInfo && (
        <div className="restore-banner">
          <div className="restore-banner-text">
            <span>💾</span>
            <span>
              <strong>Unsaved session found</strong> — {restoreInfo.data.length}{" "}
              row
              {restoreInfo.data.length !== 1 ? "s" : ""} from{" "}
              <strong>{formatSavedAt(restoreInfo.savedAt)}</strong>. Restore?
            </span>
          </div>
          <div className="restore-banner-actions">
            <button className="restore-yes-btn" onClick={handleRestore}>
              ✅ Restore
            </button>
            <button className="restore-no-btn" onClick={handleDiscardRestore}>
              🗑 Discard
            </button>
          </div>
        </div>
      )}

      {importError && (
        <div className="error-banner">
          <div className="error-banner-content">
            <div className="error-banner-header">
              <span>❌ {importError.title}</span>
              <button
                className="error-banner-close"
                onClick={() => setImportError(null)}
              >
                ×
              </button>
            </div>
            <div className="error-banner-message">{importError.message}</div>
            {importError.unexpected && (
              <div className="error-columns">
                <div className="error-columns-label">Unrecognized columns:</div>
                <div className="error-columns-list">
                  {importError.unexpected.join(", ")}
                </div>
              </div>
            )}
            {importError.missing && (
              <div className="error-columns">
                <div className="error-columns-label">Missing:</div>
                <div className="error-columns-list">
                  {importError.missing.join(", ")}
                </div>
              </div>
            )}
            {importError.expected && (
              <div className="error-columns">
                <div className="error-columns-label">Required:</div>
                <div className="error-columns-list">
                  {importError.expected.join(", ")}
                  {dynamicPrefixes.length > 0 && (
                    <span style={{ color: "#B87333", fontStyle: "italic" }}>
                      {" "}
                      + "{dynamicPrefixes.join('", "')}" columns
                    </span>
                  )}
                </div>
              </div>
            )}
            {importError.found && (
              <div className="error-columns">
                <div className="error-columns-label">Found in file:</div>
                <div className="error-columns-list">
                  {importError.found.join(", ")}
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      {importWarning && !importError && (
        <div className="warning-banner">
          <div className="warning-banner-content">
            <div className="warning-banner-header">
              <span>⚠️ {importWarning.title}</span>
              <button
                className="warning-banner-close"
                onClick={() => setImportWarning(null)}
              >
                ×
              </button>
            </div>
            <div className="warning-banner-message">
              {importWarning.message}
            </div>
            {importWarning.missing && (
              <div className="warning-columns">
                <div className="warning-columns-label">
                  Missing columns (added as empty):
                </div>
                <div className="warning-columns-list">
                  {importWarning.missing.join(", ")}
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      <input
        type="file"
        ref={fileInputRef}
        className="import-file-input"
        accept=".xlsx,.xls"
        onChange={handleImportFile}
      />

      <div>
        <div className="page-heading">{config.title}</div>
        <div className="page-subheading">
          {config.description}
          {dynamicColumns.length > 0 && (
            <span style={{ marginLeft: 8, color: "#003594", fontWeight: 600 }}>
              ({dynamicColumns.length} Scenario column
              {dynamicColumns.length !== 1 ? "s" : ""} loaded)
            </span>
          )}
        </div>
      </div>

      {conversion && (
        <div className="convert-panel">
          <div className="convert-panel-title">Upload Source File</div>
          <div className="convert-panel-hint">
            Accepted: {conversion.fileLabel}
          </div>
          <div className="convert-panel-body">
            <div style={{ display: "flex", flexDirection: "column" }}>
              <input
                type="file"
                ref={convertFileRef}
                style={{ display: "none" }}
                accept={conversion.acceptTypes}
                onChange={handleConvertFileChange}
              />
              <button
                className="convert-upload-btn"
                onClick={() => convertFileRef.current?.click()}
                disabled={converting}
              >
                Choose File
              </button>
              {convertFile && (
                <span className="convert-file-name" title={convertFile.name}>
                  {convertFile.name}
                </span>
              )}
            </div>
            <div style={{ display: "flex", flexDirection: "column" }}>
              <button
                className="translate-btn"
                onClick={handleTranslate}
                disabled={!convertFile || converting}
              >
                {converting ? (
                  <>
                    <ReactLoading
                      type="spin"
                      color="#00205B"
                      height={18}
                      width={18}
                    />{" "}
                    Translating...
                  </>
                ) : (
                  <>Translate to BioRECIPE</>
                )}
              </button>
              {convertSuccess && !converting && (
                <span className="convert-status convert-success">
                  ✅ Done — {data.length} row{data.length !== 1 ? "s" : ""}{" "}
                  loaded
                </span>
              )}
            </div>
          </div>
        </div>
      )}

      <div
        className="table-info"
        style={{ display: "flex", alignItems: "center", gap: "12px" }}
      >
        <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
          <span>
            {data.length} row{data.length !== 1 ? "s" : ""}
          </span>
          {annotatable && (
            <span style={{ color: "#F57C00", fontWeight: 600 }}>
              · {Object.keys(annotationsMap).length} annotated
            </span>
          )}
          {haf && (
            <button
              onClick={() => setFilterText({})}
              style={{
                padding: "3px 10px",
                fontSize: "11px",
                border: "1px solid #C8C9C7",
                borderRadius: "4px",
                background: "#fff",
                cursor: "pointer",
                color: "#00205B",
              }}
            >
              Clear Filters
            </button>
          )}
          {isCreatePage && lastSavedAt && (
            <span className="autosave-indicator">
              ✓ Auto-saved {formatSavedAt(lastSavedAt)}
              <button
                onClick={handleClearSave}
                title="Clear saved data"
                style={{
                  background: "none",
                  border: "none",
                  cursor: "pointer",
                  color: "#FF5B45",
                  fontSize: "11px",
                  padding: "0 2px",
                  fontWeight: 600,
                }}
              >
                × Clear
              </button>
            </span>
          )}
        </div>
        <div className="info-btn-row">
          {importButton && (
            <button
              className="import-btn"
              onClick={handleImportClick}
              disabled={importLoading}
              style={
                importLoading ? { opacity: 0.5, cursor: "not-allowed" } : {}
              }
            >
              {importLoading ? (
                <>
                  <ReactLoading
                    type="spin"
                    color="#00205B"
                    height={18}
                    width={18}
                  />{" "}
                  Importing...
                </>
              ) : (
                <>
                  <Img src={import_logo} /> Import Data
                </>
              )}
            </button>
          )}
          <button
            className="download-btn"
            onClick={handleDownload}
            disabled={!data?.length}
            style={
              !data?.length ? { opacity: 0.4, cursor: "not-allowed" } : {}
            }
          >
            <Img src={download_logo} /> Download Excel
          </button>
        </div>
      </div>

      <EditableTable
        data={data}
        setData={setData}
        columns={COLUMNS}
        filterText={filterText}
        setFilterText={setFilterText}
        conversion={conversion}
        isLoading={importLoading || converting}
        annotatable={annotatable}
        annotationsMap={annotationsMap}
        setAnnotationsMap={setAnnotationsMap}
      />

      <div className="button-row">
        <button className="add-row-btn" onClick={handleAddRow}>
          <span style={{ fontSize: "18px", lineHeight: 1 }}>+</span> Add Row
        </button>
      </div>
    </>
  );
};

// ─────────────────────────────────────────────
// UNSAVED CHANGES MODAL
// ─────────────────────────────────────────────

const UnsavedChangesModal = ({ onStay, onLeave }) => (
  <div className="unsaved-modal-overlay">
    <div className="unsaved-modal-box">
      <div className="unsaved-modal-title">⚠️ Unsaved Changes</div>
      <p className="unsaved-modal-message">
        You have data on this page that hasn't been downloaded. Leaving now will
        cause you to <strong>lose all your data</strong>. Please download your
        file before navigating away.
      </p>
      <div className="unsaved-modal-actions">
        <button className="unsaved-modal-stay-btn" onClick={onStay}>
          Stay &amp; Download
        </button>
        <button className="unsaved-modal-leave-btn" onClick={onLeave}>
          Leave Anyway
        </button>
      </div>
    </div>
  </div>
);

// ─────────────────────────────────────────────
// MAIN APP
// ─────────────────────────────────────────────

// The page layout never scrolls horizontally (only the table wrapper does).
// Browsers still scroll "overflow: hidden/auto" ancestors sideways when a field
// inside them is focused, which shifted the whole page. Snap straight back.
const lockHScroll = (e) => {
  if (e.currentTarget.scrollLeft !== 0) e.currentTarget.scrollLeft = 0;
};

const BiorecipeAPP = () => {
  const [activePage, setActivePage] = useState(null);
  const [pendingPage, setPendingPage] = useState(null);
  const [showUnsavedModal, setShowUnsavedModal] = useState(false);
  const pageDataRef = useRef([]);

  const ac = activePage ? PAGE_CONFIGS[activePage] : null;

  const registerPageData = useCallback((data) => {
    pageDataRef.current = data;
  }, []);

  const hasUnsavedData = useCallback(() => {
    if (!activePage) return false;
    const cfg = PAGE_CONFIGS[activePage];
    const currentData = pageDataRef.current;
    if (!currentData || currentData.length === 0) return false;
    // Create pages autosave to localStorage, so no need to warn
    if (cfg.hasImportData) return false;
    return currentData.length > 0;
  }, [activePage]);

  const handlePageSelect = useCallback(
    (newPageId) => {
      if (newPageId === activePage) return;
      if (hasUnsavedData()) {
        setPendingPage(newPageId);
        setShowUnsavedModal(true);
      } else {
        pageDataRef.current = [];
        setActivePage(newPageId);
      }
    },
    [activePage, hasUnsavedData],
  );

  const handleStay = useCallback(() => {
    setShowUnsavedModal(false);
    setPendingPage(null);
  }, []);

  const handleLeave = useCallback(() => {
    setShowUnsavedModal(false);
    pageDataRef.current = [];
    setActivePage(pendingPage);
    setPendingPage(null);
  }, [pendingPage]);

  return (
    <>
      <style>{styles}</style>
      {showUnsavedModal && (
        <UnsavedChangesModal onStay={handleStay} onLeave={handleLeave} />
      )}
      <div className="page-wrapper" onScroll={lockHScroll}>
        <div className="body-layout" onScroll={lockHScroll}>
          <nav className="sidebar">
            <SidebarTree
              activePage={activePage}
              onPageSelect={handlePageSelect}
            />
            <div className="sidebar-section">
              <div className="sidebar-section-title">Tools</div>
              <Link to="/analyzer" rel="noopener noreferrer" className="sidebar-item">
                <span>Analyzer</span>
              </Link>
            </div>
            <div className="sidebar-footer">
              <div className="sidebar-footer-text">
                <Link
                  to="/about"
                  state={{ fromTool: "biorecipe" }}
                  rel="noopener noreferrer"
                  style={{
                    color: "#FFB81C",
                    textDecoration: "none",
                    margin: "0 4px",
                  }}
                >
                  About
                </Link>
                {" · "}
                <a
                  href="https://github.com/pitt-miskov-zivanov-lab/BioRECIPE/blob/main/docs/introduction.rst"
                  target="_blank"
                  rel="noopener noreferrer"
                  style={{
                    color: "#FFB81C",
                    textDecoration: "none",
                    margin: "0 4px",
                  }}
                >
                  GitHub
                </a>
                {" · "}
                <a
                  href="https://melody-biorecipe.readthedocs.io/en/latest/introduction.html"
                  target="_blank"
                  rel="noopener noreferrer"
                  style={{
                    color: "#FFB81C",
                    textDecoration: "none",
                    margin: "0 4px",
                  }}
                >
                  ReadTheDocs
                </a>
                <div
                  style={{
                    marginTop: "6px",
                    fontSize: "10px",
                    color: "rgba(219,238,255,0.7)",
                  }}
                >
                  © 2026{" "}
                  <Link
                    to="https://www.nmzlab.pitt.edu/"
                    target="_blank"
                    rel="noopener noreferrer"
                    style={{ color: "rgba(219,238,255,0.7)" }}
                  >
                    Miskov-Zivanov Lab (MeLoDy lab)
                  </Link>
                </div>
              </div>
            </div>
          </nav>
          <main className="main-area" onScroll={lockHScroll}>
            {ac ? (
              <TablePage
                key={activePage}
                pageId={activePage}
                config={ac}
                registerPageData={registerPageData}
              />
            ) : (
              <div className="welcome-container">
                <div className="welcome-icon">📂</div>
                <div className="welcome-title">Welcome to BioRECIPE</div>
                <div className="welcome-subtitle">
                  Select an item from the sidebar to get started.
                </div>
              </div>
            )}
          </main>
        </div>
      </div>
    </>
  );
};

export default BiorecipeAPP;